import { PreviewMessage, ThinkingMessage } from "./message";
import type { Vote } from "@/lib/db/schema";
import type { UIMessage } from "ai";
import { forwardRef, memo, useEffect, useMemo, useRef, type HTMLAttributes, type MutableRefObject } from "react";
import equal from "fast-deep-equal";
import type { UIArtifact } from "./artifact";
import type { UseChatHelpers } from "@ai-sdk/react";
import { useMessages } from "@/hooks/use-messages";
import { ModeToggle } from "./mode-toggle";
import { Greeting } from "./greeting";
import { Bell } from "lucide-react";
import type { TradingChartRef } from "./TradingChart/TradingChart";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import {
  clearOutlineTurns,
  deriveOutlineTurns,
  setOutlineJump,
  setOutlineTurns,
} from "./chat-outline/outline-store";

interface ArtifactMessagesProps {
  chatId: string;
  status: UseChatHelpers["status"];
  votes: Array<Vote> | undefined;
  messages: Array<UIMessage>;
  setMessages: UseChatHelpers["setMessages"];
  reload: UseChatHelpers["reload"];
  isReadonly: boolean;
  artifactStatus: UIArtifact["status"];
  chartRef?: MutableRefObject<TradingChartRef | null>;
  append?: UseChatHelpers["append"];
  subAgentMessageIdsRef?: MutableRefObject<Set<string>>;
  viewMode?: string;
  alertStartCellIndex?: number | null;
  /**
   * When true, the empty-state (messages.length === 0) renders a compact,
   * alert-page-tuned card instead of the full-width `<Greeting />` hero.
   * Only the /alerts/[id] route passes this through; /chat/[id] leaves it
   * undefined so the existing landing hero is preserved.
   */
  isAlertEmpty?: boolean;
}

function hasAssistantResponseAfterLatestUser(messages: Array<UIMessage>) {
  const latestUserIndex = messages.findLastIndex(
    (message) => message.role === "user"
  );
  if (latestUserIndex === -1) return false;

  return messages.slice(latestUserIndex + 1).some((message) => {
    if (message.role !== "assistant") return false;
    if (typeof message.content === "string" && message.content.trim()) {
      return true;
    }
    return Array.isArray(message.parts) && message.parts.length > 0;
  });
}

function PureArtifactMessages({
  chatId,
  status,
  votes,
  messages,
  setMessages,
  reload,
  isReadonly,
  chartRef,
  append,
  subAgentMessageIdsRef,
  viewMode,
  alertStartCellIndex,
  isAlertEmpty,
}: ArtifactMessagesProps) {
  const {
    containerRef: messagesContainerRef,
    onViewportEnter,
    onViewportLeave,
    hasSentMessage,
  } = useMessages({
    chatId,
    status,
  });

  // Stable Scroller identity — see messages.tsx for the full rationale.
  // Inline/recreated Scroller makes Virtuoso remount the scroll container
  // (flash + jump-to-top). https://virtuoso.dev/react-virtuoso/troubleshooting/
  const containerRefHolder = useRef(messagesContainerRef);
  containerRefHolder.current = messagesContainerRef;
  const onViewportEnterRef = useRef(onViewportEnter);
  onViewportEnterRef.current = onViewportEnter;
  const onViewportLeaveRef = useRef(onViewportLeave);
  onViewportLeaveRef.current = onViewportLeave;

  const Scroller = useMemo(
    () =>
      forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
        ({ onScroll, className, ...props }, ref) => (
          <div
            {...props}
            ref={(node) => {
              containerRefHolder.current.current = node;
              if (typeof ref === "function") ref(node);
              else if (ref) (ref as MutableRefObject<HTMLDivElement | null>).current = node;
            }}
            data-chat-messages
            className={`${className ?? ""} h-full overflow-y-auto relative`}
            onScroll={(event) => {
              onScroll?.(event);
              const container = event.currentTarget;
              const threshold = 50;
              const isAtBottom =
                container.scrollTop + container.clientHeight >=
                container.scrollHeight - threshold;

              if (isAtBottom) {
                onViewportEnterRef.current();
              } else {
                onViewportLeaveRef.current();
              }
            }}
          />
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  Scroller.displayName = "VirtualizedArtifactMessagesScroller";

  // Feed the ChatOutline timeline from the in-memory message list (not the
  // DOM — virtualization unmounts off-screen messages). See messages.tsx.
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  useEffect(() => {
    setOutlineJump((index) =>
      virtuosoRef.current?.scrollToIndex({
        index,
        align: "center",
        behavior: "smooth",
      }),
    );
    return () => {
      setOutlineJump(null);
      clearOutlineTurns();
    };
  }, []);
  useEffect(() => {
    setOutlineTurns(deriveOutlineTurns(messages));
  }, [messages]);

  // Same footer rule as the main chat path: once the assistant has started
  // rendering text/tool cards after the latest user message, the extra global
  // "Working" row is stale and should disappear.
  const shouldShowWorkingFooter =
    status === "submitted" &&
    messages[messages.length - 1]?.role === "user" &&
    !hasAssistantResponseAfterLatestUser(messages);

  const virtuosoComponents = useMemo(() => {
    const Footer = () => {
      return shouldShowWorkingFooter ? (
        <ThinkingMessage />
      ) : (
        <div className="shrink-0 min-w-[24px] min-h-[24px]" />
      );
    };
    Footer.displayName = "VirtualizedArtifactMessagesFooter";
    return { Scroller, Footer };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Scroller, shouldShowWorkingFooter]);

  if (messages.length === 0) {
    return (
      <div
        ref={messagesContainerRef}
        data-chat-messages
        className="flex flex-col gap-0 md:gap-4 items-center"
      >
        {isAlertEmpty ? (
          <div className="w-full flex flex-col items-center px-4 pt-2 pb-5">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${viewMode === "signal" ? "bg-emerald-300/10 ring-1 ring-emerald-300/25" : "bg-sky-300/10 ring-1 ring-sky-300/25"}`}>
              <Bell className={`w-4 h-4 ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`} strokeWidth={1.8} />
            </div>
            <h2 className="mt-3 text-[15px] font-semibold text-slate-100 text-center leading-snug">
              Ask Xynth about this {viewMode === "signal" ? "signal" : "alert"}
            </h2>
            <p className="mt-1.5 max-w-[260px] text-[12.5px] text-slate-400 text-center leading-relaxed">
              Edit cells, change the threshold, pause or resume the schedule,
              or commit changes with <span className="text-slate-300">upload_alert</span>.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Greeting />
            <ModeToggle />
          </div>
        )}
      </div>
    );
  }

  return (
    <Virtuoso
      ref={virtuosoRef}
      className="h-full"
      data={messages}
      initialTopMostItemIndex={messages.length - 1}
      followOutput={(isAtBottom) => (isAtBottom ? "smooth" : false)}
      atBottomStateChange={(atBottom) => {
        if (atBottom) onViewportEnter();
        else onViewportLeave();
      }}
      increaseViewportBy={{ top: 800, bottom: 1200 }}
      defaultItemHeight={220}
      itemContent={(index, message) => (
        <div className="pb-0 md:pb-4">
          <PreviewMessage
            chatId={chatId}
            key={message.id}
            message={message}
            isLoading={status === "streaming" && index === messages.length - 1}
            vote={
              votes
                ? votes.find((vote) => vote.messageId === message.id)
                : undefined
            }
            setMessages={setMessages}
            reload={reload}
            isReadonly={isReadonly}
            requiresScrollPadding={
              hasSentMessage && index === messages.length - 1
            }
            chartRef={chartRef}
            append={append}
            isLastMessage={index === messages.length - 1}
            subAgentMessageIdsRef={subAgentMessageIdsRef}
            viewMode={viewMode}
            alertStartCellIndex={alertStartCellIndex}
          />
        </div>
      )}
      components={virtuosoComponents}
    />
  );
}

function areEqual(
  prevProps: ArtifactMessagesProps,
  nextProps: ArtifactMessagesProps
) {
  if (
    prevProps.artifactStatus === "streaming" &&
    nextProps.artifactStatus === "streaming"
  )
    return true;

  if (prevProps.status !== nextProps.status) return false;
  if (prevProps.status && nextProps.status) return false;
  if (prevProps.messages.length !== nextProps.messages.length) return false;
  if (!equal(prevProps.messages, nextProps.messages)) return false;
  if (!equal(prevProps.votes, nextProps.votes)) return false;
  if (prevProps.viewMode !== nextProps.viewMode) return false;
  if (prevProps.alertStartCellIndex !== nextProps.alertStartCellIndex) return false;
  if (prevProps.isAlertEmpty !== nextProps.isAlertEmpty) return false;
  return true;
}

export const ArtifactMessages = memo(PureArtifactMessages, areEqual);
